// 공정 체크 화면 (목록 아코디언 · QR · 앱 WebView 공용 1개)
// 정본: docs/기획서_QR공정체크_2026-08.md §5-3 / 시안 qr_preview_1.html
//
// 이 컴포넌트는 저장하지 않는다. onToggle(key, nextValue)를 부모가 처리한다.
// ★ 부모 구현 규약(P2-20): onToggle은 저장 실패 시 **반드시 예외를 던지거나 reject된 Promise를 돌려줘야** 한다.
//   내부에서 try/catch로 삼키고 정상 반환하면 이 화면은 성공으로 간주해 체크가 켜진 채 남는다
//   (= 저장 안 된 체크가 저장된 것처럼 보이는 조용한 실패). 롤백은 이 예외를 받아야만 일어난다.
// 표시 금지(확정): 금액·단가, 환자 연락처, 다른 케이스로 가는 링크.
import React, { useState, useEffect } from 'react';
import { deriveChecks, formatCheckTime, formatDday, toDate } from '../utils/caseChecks';

// ProductionBoard 팔레트와 같은 계열 (인라인 스타일 컨벤션 유지)
const C = {
    ink: '#1e293b',
    sub: '#64748b',
    muted: '#94a3b8',
    line: '#e2e8f0',
    blue: '#6366f1',   // 2026-08-18 디자인 통일 — 보드류 파랑 → 보라
    red: '#d03b3b',
    green: '#0ca30c',
    amber: '#d97706',
};

const S = {
    wrap: { fontFamily: 'inherit', color: C.ink },
    name: { fontSize: 22, fontWeight: 800, lineHeight: 1.25, wordBreak: 'break-all' },
    sub: { fontSize: 13.5, color: C.sub, marginTop: 3 },
    remake: {
        display: 'block', background: '#fdf3f3', border: '1px solid rgba(208,59,59,0.3)',
        color: '#8a2b2b', borderRadius: 8, padding: '7px 10px', fontSize: 12.5,
        fontWeight: 800, marginBottom: 8,
    },
    ddayBox: { margin: '12px 0 3px' },
    dday: { fontSize: 17, fontWeight: 800 },
    ddaySub: { fontSize: 11.5, color: C.muted, marginBottom: 8 },
    hr: { height: 1, background: C.line, margin: '8px 0' },
    btn: {
        width: '100%', minHeight: 52, borderRadius: 12, border: `1.5px solid ${C.line}`,
        background: '#fff', color: C.ink, fontSize: 15, fontWeight: 700, fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 13px', marginTop: 7, cursor: 'pointer', textAlign: 'left',
    },
    btnOn: { background: '#eef7ee', borderColor: 'rgba(12,163,12,0.45)', color: '#116b11' },
    btnAuto: { background: '#f6f6f4', color: '#4b5563' },
    btnLeft: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 },
    chk: {
        width: 20, height: 20, borderRadius: 6, border: '1.5px solid #cbd5e1',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, color: 'transparent', flexShrink: 0,
    },
    chkOn: { background: C.green, borderColor: C.green, color: '#fff' },
    chkAuto: { background: '#9ca3af', borderColor: '#9ca3af', color: '#fff' },
    time: { fontSize: 11.5, fontWeight: 600, color: C.muted, whiteSpace: 'nowrap' },
    timeOn: { color: '#2f7a2f' },
    tag: { fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 5, background: 'rgba(11,11,11,0.06)', color: C.sub },
    tagRev: { background: '#fef3c7', color: '#92400e' },
    fold: { fontSize: 12.5, color: C.sub, padding: '9px 2px 2px', cursor: 'pointer' },
    foldBody: { fontSize: 12, color: C.sub, lineHeight: 1.85, padding: '4px 2px 2px' },
    err: {
        background: '#fdf3f3', border: '1px solid rgba(208,59,59,0.3)', color: '#8a2b2b',
        borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.6, marginTop: 8,
    },
};

// ── 표시값 추출 (workOrders · parsedCases 두 스키마 모두 대응) ──
const patientOf = (e) => e?._name || e?.patientName || e?.patient || '(환자명 없음)';
const clinicOf = (e) => e?._clinic || e?.partnerName || e?.clinicName || e?.fromCompanyName || e?.dentalName || '';

const teethOf = (e) => {
    const raw = Array.isArray(e?.selectedTeeth) ? e.selectedTeeth
        : Array.isArray(e?.teeth) ? e.teeth
            : Array.isArray(e?.teethDetails) ? e.teethDetails.map(d => d?.tooth || d?.toothNumber)
                : (e?.teethDetails && typeof e.teethDetails === 'object') ? Object.keys(e.teethDetails)
                    : [];
    return raw.filter(Boolean).map(t => (String(t).startsWith('#') ? String(t) : `#${t}`));
};

const detailLines = (e) => {
    const lines = [];
    const prosthesis = e?.prosthesis || (Array.isArray(e?.indications) ? e.indications.filter(Boolean).join(', ') : '');
    const material = e?.material || '';
    const shade = e?.shade || e?.selectedShade || '';
    if (prosthesis || material) lines.push([prosthesis, material].filter(Boolean).join(' · '));
    if (shade) lines.push(`쉐이드 ${shade}`);
    const orderNo = e?.orderNumber || e?.caseNo || e?.caseNumber || '';
    if (orderNo) lines.push(`주문번호 ${orderNo}`);
    const created = toDate(e?.createdAt) || toDate(e?.receivedAt) || toDate(e?.fileMtime);
    if (created) lines.push(`접수 ${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}-${String(created.getDate()).padStart(2, '0')}`);
    const chart = e?.chartNumber || e?.chartNo || '';
    if (chart) lines.push(`차트번호 ${chart}`);
    const req = e?.requests || e?.specialInstructions || e?.notes || e?.memo || '';
    if (req) lines.push(`요청: ${String(req).slice(0, 200)}`);
    return lines;
};

// 저장값(processStage) 이력 → 공정 3단계 문구(2026-10-12 대표 확정: 의뢰서 확인/디자인 완료/포장 완료 — utils/shared/clinic-stage.js stage3Of 와 같은 묶음).
//   괄호 안은 저장된 세부 신호. 밀링(milling)은 데이터 그대로, 표시만 「디자인 완료」. (구 2026-09-29 4단계: 접수/확인/디자인완료/밀링완료)
const STAGE_LABEL = {
    received: '의뢰서 확인 (열람)', scanning: '의뢰서 확인 (스캔 감지)', designing: '의뢰서 확인 (디자인 시작)',
    milling: '디자인 완료', shipping: '포장 완료 (구 단계 기록)', done: '완료',   // shipping = 2026-09-29 이전 4번째 열(포장완료) 저장값 — stage3Of도 포장 완료로 본다
};

const timelineOf = (e) => {
    const rows = [];
    const hist = Array.isArray(e?.stageHistory) ? e.stageHistory : [];
    hist.forEach(h => {
        const at = toDate(h?.at);
        if (!h?.stage) return;
        rows.push({
            at,
            text: `${STAGE_LABEL[h.stage] || h.stage}${at ? ' · ' + formatCheckTime(at) : ''}`
                + (h.source === 'auto' ? ' (자동)' : h.source === 'auto_due' ? ' (자동 · 납기일 경과)' : ''),
        });
    });
    if (e?.designDone === true) rows.push({ at: toDate(e?.designDoneAt), text: '디자인 완료' });
    rows.sort((a, b) => (a.at ? a.at.getTime() : 0) - (b.at ? b.at.getTime() : 0));
    return rows;
};

const dateLabel = (d) => {
    if (!d) return '';
    const wd = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${wd})`;
};

function Fold({ title, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <div style={S.fold} onClick={() => setOpen(v => !v)}>{(open ? '▾ ' : '▸ ') + title}</div>
            {open && <div style={S.foldBody}>{children}</div>}
        </div>
    );
}

export default function CaseCheckView({ entity, onToggle }) {
    // 낙관 표시 — 누른 즉시 반영하고, 저장 실패하면 되돌린다
    const [pending, setPending] = useState({});
    const [error, setError] = useState('');

    const derived = deriveChecks(entity);

    // 낙관 표시 해제 = 부모 스냅샷이 같은 값을 실어온 순간 (종전 1.5초 타이머는 제거 — P2-19)
    // 저장은 됐는데 스냅샷이 늦으면 타이머가 먼저 끝나 체크가 잠깐 풀렸다 다시 켜지는 깜빡임이 있었다.
    // 스냅샷이 영영 안 오면 낙관 표시가 유지된다(저장은 성공한 상태이므로 값이 되돌아가지는 않는다).
    useEffect(() => {
        setPending(prev => {
            const keys = Object.keys(prev);
            if (keys.length === 0) return prev;
            const items = deriveChecks(entity).items;
            const next = { ...prev };
            let changed = false;
            keys.forEach(k => {
                const it = items.find(i => i.key === k);
                if (it && it.done === prev[k].next) { delete next[k]; changed = true; }
            });
            return changed ? next : prev;
        });
    }, [entity]);
    const dday = formatDday(derived.deadline, derived.deadlineBasis);
    const teeth = teethOf(entity);
    const clinic = clinicOf(entity);
    const details = detailLines(entity);
    const timeline = timelineOf(entity);

    const viewOf = (item) => {
        const p = pending[item.key];
        if (!p) return item;
        // 낙관 상태: 켜면 사람 체크(자동 아님), 끄면 자동이었을 경우 '되돌림'
        return {
            ...item,
            done: p.next,
            at: p.next ? p.at : null,
            auto: false,
            reverted: !p.next && item.auto ? true : (p.next ? false : item.reverted),
        };
    };

    const handleClick = async (item) => {
        const cur = viewOf(item);
        const next = !cur.done;
        if (!next) {
            if (item.key === 'packing'
                && !window.confirm('포장 완료를 취소하시겠습니까?\n치과에 이미 알림이 나갔습니다.')) return;
            if (item.key !== 'packing' && (item.auto || cur.auto)
                && !window.confirm('프로그램이 자동으로 확인한 항목입니다.\n손으로 되돌리시겠습니까?')) return;
        }
        setError('');
        setPending(prev => ({ ...prev, [item.key]: { next, at: new Date() } }));
        try {
            // 부모가 실패를 삼키면 롤백이 일어나지 않는다(위 규약 참조) — 반드시 throw/reject 되어야 한다
            if (typeof onToggle === 'function') await onToggle(item.key, next);
        } catch (e) {
            // 조용한 실패 금지 — 화면 롤백 + 사유 표시
            console.error('공정 체크 저장 실패:', item.key, e?.message || e);
            setError(`저장하지 못했습니다 — ${item.label}. ${e?.message || '네트워크를 확인하고 다시 눌러 주세요.'}`);
            setPending(prev => { const n = { ...prev }; delete n[item.key]; return n; });
            return;
        }
        // 성공: 낙관 표시는 부모 스냅샷이 같은 값을 실어올 때 위 useEffect가 해제한다(타이머 없음)
    };

    return (
        <div style={S.wrap}>
            {entity?.isRemake === true && <span style={S.remake}>{'리메이크 건'}</span>}

            {/* ① 환자명 · 치과명 · 치식 */}
            <div style={S.name}>{patientOf(entity)}</div>
            <div style={S.sub}>{[clinic, teeth.join(' ')].filter(Boolean).join(' · ') || '치과 미확인'}</div>

            {/* ② 마감일 */}
            <div style={S.ddayBox}>
                <div style={{ ...S.dday, color: dday && dday.state === 'late' ? C.red : (dday && dday.state === 'today' ? C.amber : C.ink) }}>
                    {dday ? dday.text : '마감일 없음'}
                </div>
                <div style={S.ddaySub}>
                    {dday ? dateLabel(derived.deadline) : '내원일·납기가 등록되어 있지 않습니다'}
                    {derived.deadlineBasis === 'due' && dday ? ' · 납기 기준' : ''}
                </div>
            </div>

            <div style={S.hr} />

            {/* ③ 체크 6개 (작업 공정 순 고정) */}
            {derived.items.map(item => {
                const v = viewOf(item);
                // 적용 안 되는 공정(예: 임플란트 아닌 케이스의 지그)은 흐리게 — 누르면 수동 추가된다 (대표 확정 08-09)
                const notApplicable = item.applicable === false && !v.done;
                const btnStyle = {
                    ...S.btn,
                    ...(v.done ? (v.auto ? S.btnAuto : S.btnOn) : {}),
                    ...(notApplicable ? { opacity: 0.45, minHeight: 44 } : {}),
                };
                const chkStyle = { ...S.chk, ...(v.done ? (v.auto ? S.chkAuto : S.chkOn) : {}) };
                return (
                    <button key={item.key} type="button" style={btnStyle} onClick={() => handleClick(item)}>
                        <span style={S.btnLeft}>
                            <span style={chkStyle}>{'✓'}</span>
                            <span>{item.label}</span>
                            {v.auto && <span style={S.tag}>{'자동'}</span>}
                            {v.reverted && <span style={{ ...S.tag, ...S.tagRev }}>{'되돌림'}</span>}
                            {notApplicable && <span style={S.tag}>{'이 케이스엔 없음'}</span>}
                        </span>
                        <span style={{ ...S.time, ...(v.done && !v.auto ? S.timeOn : {}) }}>
                            {v.done ? formatCheckTime(v.at) : ''}
                        </span>
                    </button>
                );
            })}

            {error && <div style={S.err}>{error}</div>}

            {/* ④ 진행 과정 */}
            <Fold title="진행 과정 보기">
                {timeline.length === 0
                    ? '아직 기록된 진행 과정이 없습니다.'
                    : timeline.map((r, i) => <div key={i}>{r.text}</div>)}
            </Fold>

            {/* ⑤ 자세히 (금액·단가·연락처 없음) */}
            <Fold title="자세히 (재료 · 쉐이드 · 요청사항)">
                {details.length === 0
                    ? '등록된 상세 정보가 없습니다.'
                    : details.map((t, i) => <div key={i}>{t}</div>)}
            </Fold>
        </div>
    );
}
